var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { UTCDateTimeStringToDateString } from '../../_internal/conversion/converters/UTCDateTimeStringToDate';
import { convert } from '../../_internal/conversion/module';
import { CompanionError } from '../../_internal/error/CompanionError';
import { isHttpError } from '../../_internal/error/HttpError';
import { validateStringArgumentNotEmpty } from '../../_internal/validation';
import * as api from '../../api/module';
import { paramsToString, parseRequestOptions } from '../../api/util';
import { signUrls } from '../../sites';
import { validateURLData } from '../upload/upload-url';
const conversionConfig = {
    created: UTCDateTimeStringToDateString,
    last_modified: UTCDateTimeStringToDateString,
    'metadata.publish_start_date': UTCDateTimeStringToDateString,
    'metadata.publish_end_date': UTCDateTimeStringToDateString,
};
/**
 * Retrieve a list of media that matches the specified query parameters.
 */
export function listMedia(params, requestOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        requestOptions = parseRequestOptions(requestOptions);
        const queryParams = paramsToString(params);
        const res = yield api.list(`/v2/sites/${requestOptions.siteId}/media/${queryParams}`, Object.assign({ requiresAuth: true }, requestOptions));
        return {
            raw: res,
            content: Object.assign(Object.assign({}, res.data), { media: res.data.media.map((mediaItem) => convert('toClient', mediaItem, conversionConfig)) }),
        };
    });
}
/**
 * Retrieve media by ID
 */
export function getMedia(id, requestOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        requestOptions = parseRequestOptions(requestOptions);
        validateStringArgumentNotEmpty(id, 'id');
        const res = yield api.get(`/v2/sites/${requestOptions.siteId}/media/${id}/`, Object.assign({}, requestOptions));
        return {
            raw: res,
            content: convert('toClient', res.data, conversionConfig),
        };
    });
}
/**
 * Create new media
 */
export function createMedia(data, requestOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        requestOptions = parseRequestOptions(requestOptions);
        validateURLData(data); // Will verify and modify the data for URL uploading/ linking if required
        const res = yield api.post(`/v2/sites/${requestOptions.siteId}/media/`, Object.assign({ data: convert('toServer', data, conversionConfig) }, requestOptions));
        return {
            raw: res,
            content: convert('toClient', res.data, conversionConfig),
        };
    });
}
/**
 * Update existing media
 */
export function updateMedia(id, data, requestOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        requestOptions = parseRequestOptions(requestOptions);
        validateStringArgumentNotEmpty(id, 'id');
        const res = yield api.patch(`/v2/sites/${requestOptions.siteId}/media/${id}/`, Object.assign({ data: convert('toServer', data, conversionConfig) }, requestOptions));
        return {
            raw: res,
            content: convert('toClient', res.data, conversionConfig),
        };
    });
}
/**
 * Delete media
 */
export function deleteMedia(ids, requestOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        requestOptions = parseRequestOptions(requestOptions);
        let res;
        if (typeof ids === 'string') {
            validateStringArgumentNotEmpty(ids, 'id');
            res = yield api.remove(`/v2/sites/${requestOptions.siteId}/media/${ids}`, requestOptions);
        }
        else {
            res = yield api.put(`/v2/sites/${requestOptions.siteId}/media/bulk_delete`, Object.assign({ data: {
                    media_ids: ids,
                } }, requestOptions));
        }
        return {
            raw: res,
        };
    });
}
/**
 * Reupload media (for replacing original upload or trimming assets)
 * Appears in media module because it is nested in the /media endpoint
 */
export function reuploadMedia(id, data, requestOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        requestOptions = parseRequestOptions(requestOptions);
        validateStringArgumentNotEmpty(id, 'id');
        try {
            const res = yield api.put(`/v2/sites/${requestOptions.siteId}/media/${id}/reupload`, Object.assign({ data: convert('toServer', data, conversionConfig) }, requestOptions));
            return {
                raw: res,
                content: convert('toClient', res.data, conversionConfig),
            };
        }
        catch (err) {
            let code = reuploadMedia.ERR.UNKNOWN;
            // TODO: fix the dirty string checks below once api has better error codes
            if (isHttpError(err) && err.apiErrors) {
                const desc = (_a = err.apiErrors[0]) === null || _a === void 0 ? void 0 : _a.description;
                if (desc === null || desc === void 0 ? void 0 : desc.includes('source_url')) {
                    code = reuploadMedia.ERR.INVALID_SOURCE_URL;
                }
            }
            throw new CompanionError({
                error: err,
                code,
            });
        }
    });
}
reuploadMedia.ERR = {
    UNKNOWN: 'UNKNOWN',
    INVALID_SOURCE_URL: 'INVALID_SOURCE_URL',
};
/**
 * Get the condition of a specific media, noting whether or not it is publishable in feeds
 */
export function getMediaCondition(id, requestOptions) {
    return __awaiter(this, void 0, void 0, function* () {
        requestOptions = parseRequestOptions(requestOptions);
        validateStringArgumentNotEmpty(id, 'id');
        // The conditions endpoint requires a special kind of token generated based on the v2 media path.
        // we are using a dummy url with the right path to sign that url to get that token. Do not confuse
        // this token with ../src/api-tokens.
        // Note that we are fetching a new token each time someone calls us. If you need, you can optimize this
        // by keeping each of those tokens in memory until they expire and refetch only then.
        const urlWithToken = (yield signUrls([{ resource: `https://fake.url/v2/media/${id}`, internal: true }], requestOptions)).content[0];
        const token = urlWithToken === null || urlWithToken === void 0 ? void 0 : urlWithToken.split('token=')[1];
        const res = yield api.get(`/v2/sites/${requestOptions.siteId}/media/${id}/condition?token=${token}`, requestOptions);
        return {
            raw: res,
            content: res.data,
        };
    });
}
